import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Popover, type PopoverProps } from 'tea-component';
import ColorPalette, {
  type IColorInfo,
  type IColorPaletteProps,
  type IColorPaletteRef,
} from '@/pages/datalab/components/ColorPalette';
import './index.scss';

type TPopoverPassthroughProps = Omit<
  PopoverProps,
  'overlay' | 'visible' | 'onVisibleChange' | 'children'
>;

type TColorPassthroughProps = Omit<
  IColorPaletteProps,
  'colorInfo' | 'onChangeColorInfo' | 'isClose'
>;

export interface IColorPalettePopoverRef {
  submit: () => void;
}

export interface IColorPalettePopoverProps {
  children?: React.ReactElement;
  value?: IColorInfo;
  defaultValue?: IColorInfo;
  onChange: (color: IColorInfo) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  popoverProps?: TPopoverPassthroughProps;
  onVisibleChange?: (visible: boolean) => void;
  colorProps?: TColorPassthroughProps;
}

const DEFAULT_COLOR: IColorInfo = { hexa: '#ffffff', alpha: '100' };

const ColorPalettePopover = forwardRef<
  IColorPalettePopoverRef,
  IColorPalettePopoverProps
>((props, ref) => {
  const {
    children,
    value,
    defaultValue = DEFAULT_COLOR,
    onChange,
    open: controlledOpen,
    onOpenChange,
    popoverProps,
    onVisibleChange,
    colorProps,
  } = props;

  const colorPaletteRef = useRef<IColorPaletteRef | null>(null);
  const [uncontrolledOpen, setUncontrolledOpen] = useState<boolean>(false);
  const isControlled = typeof controlledOpen === 'boolean';
  const open = isControlled ? Boolean(controlledOpen) : uncontrolledOpen;

  const currentColor = value || defaultValue;

  const submit = (): void => {
    colorPaletteRef.current?.handleOnSubmit?.();
  };

  useImperativeHandle(
    ref,
    () => ({
      submit,
    }),
    [],
  );

  const handleVisibleChange = (visible: boolean): void => {
    if (visible) {
      onVisibleChange?.(true);
      if (isControlled) {
        onOpenChange?.(true);
      } else {
        setUncontrolledOpen(true);
      }
      return;
    }

    // 任何关闭弹层的路径，都视为“确认当前颜色”。
    submit();
    onVisibleChange?.(false);

    if (isControlled) {
      onOpenChange?.(false);
    } else {
      setUncontrolledOpen(false);
    }
  };

  return (
    <Popover
      {...popoverProps}
      visible={open}
      onVisibleChange={handleVisibleChange}
      overlay={
        // 这里需要一个 div 包裹，避免因为ColorPalette第一次懒加载导致Popover定位偏差
        <div className="dbl-color-palette-popover-overlay">
          <ColorPalette
            {...colorProps}
            ref={colorPaletteRef}
            colorInfo={currentColor}
            onChangeColorInfo={onChange}
            isClose={false}
          />
        </div>
      }
    >
      {children}
    </Popover>
  );
});

export default ColorPalettePopover;
